/**
 * Guardrails for moving an appointment by drag & drop.
 *
 * These are the *only* checks standing between a drag gesture and a written record:
 * `/admin-record/edit` applies the diff it is given without re-validating availability
 * (the overlap guard lives on the create path, and the journal create call skips it),
 * so anything not rejected here becomes a real double-booking. Keep the rules here,
 * pure and tested, rather than spread through the drag handlers.
 *
 * All times are salon-zone minutes from midnight, matching `startMinOfDay`/`durationMin`
 * on normalized appointments (.clinerules/time-handling.md). Work intervals arrive from
 * the staff registry in *seconds*, which is the shape the schedule endpoints return.
 */

/** A stretch of a staff member's working day, in seconds from midnight. */
export type WorkInterval = { start: number; end: number };

/** An appointment already occupying part of the target column. */
export type OccupiedSlot = {
  id: string | number;
  startMin: number;
  durationMin: number;
};

export type MoveRejection =
  /** The new range falls outside the staff member's shift, or across a break. */
  | "outside_working_hours"
  /** Another appointment of the same staff member already covers part of the range. */
  | "slot_occupied"
  /** The staff member does not offer every service on the appointment. */
  | "services_unsupported"
  /** The staff member's service list could not be loaded, so we cannot vouch for the move. */
  | "services_unknown";

export type MoveVerdict = { ok: true } | { ok: false; reason: MoveRejection };

const OK: MoveVerdict = { ok: true };

/** Rounds a minute value to the nearest `step`, the granularity the grid snaps to. */
export function snapToStep(minutes: number, step: number): number {
  if (step <= 0) return Math.round(minutes);
  return Math.round(minutes / step) * step;
}

/**
 * Merges work intervals into sorted, non-overlapping minute ranges.
 * Touching intervals (a break of zero length) are joined, so an appointment spanning
 * the seam between two back-to-back shifts is not rejected for no reason.
 */
function mergedMinuteRanges(intervals: WorkInterval[]): { start: number; end: number }[] {
  const ranges = intervals
    .filter((iv) => Number.isFinite(iv?.start) && Number.isFinite(iv?.end) && iv.end > iv.start)
    .map((iv) => ({ start: iv.start / 60, end: iv.end / 60 }))
    .sort((a, b) => a.start - b.start);

  const merged: { start: number; end: number }[] = [];
  for (const r of ranges) {
    const last = merged[merged.length - 1];
    if (last && r.start <= last.end) {
      last.end = Math.max(last.end, r.end);
    } else {
      merged.push({ ...r });
    }
  }
  return merged;
}

/**
 * True when the whole range sits inside working time.
 *
 * Coverage is checked against merged ranges rather than any single interval, so a move
 * that starts before a break and ends after it is rejected — an appointment may not be
 * scheduled straight through the staff member's lunch.
 */
export function coversRange(
  intervals: WorkInterval[],
  startMin: number,
  durationMin: number
): boolean {
  const endMin = startMin + Math.max(1, durationMin);
  return mergedMinuteRanges(intervals).some((r) => startMin >= r.start && endMin <= r.end);
}

/**
 * The first appointment whose time overlaps the candidate range, if any.
 * `ignoreId` skips the appointment being dragged so it never collides with itself.
 */
export function findOverlap(
  occupied: OccupiedSlot[],
  startMin: number,
  durationMin: number,
  ignoreId: string | number
): OccupiedSlot | undefined {
  const endMin = startMin + Math.max(1, durationMin);
  return occupied.find((slot) => {
    if (String(slot.id) === String(ignoreId)) return false;
    const slotEnd = slot.startMin + Math.max(1, slot.durationMin);
    return slot.startMin < endMin && slotEnd > startMin;
  });
}

/**
 * Service ids the target staff member does not offer.
 *
 * `offered === undefined | null` means "not loaded" and is reported separately from
 * "loaded, and this staff member offers nothing" — the first is our own ignorance and
 * must not be presented to the user as a decision about the staff member.
 */
export function missingServices(
  required: number[],
  offered: number[] | null | undefined
): number[] | null {
  if (required.length === 0) return [];
  if (offered == null) return null;
  const have = new Set(offered.map(Number));
  return required.map(Number).filter((id) => !have.has(id));
}

export type MoveCheck = {
  /** Candidate start, salon-zone minutes from midnight. */
  startMin: number;
  /** Duration is preserved across a move; the card keeps its height. */
  durationMin: number;
  /** Target staff member's working intervals for the target date, in seconds. */
  intervals: WorkInterval[];
  /** Everything already booked for the target staff member on the target date. */
  occupied: OccupiedSlot[];
  /** Id of the appointment being moved. */
  movingId: string | number;
  /** Service ids on the appointment being moved. */
  requiredServiceIds: number[];
  /** Service ids the target staff member offers; null/undefined when not loaded yet. */
  offeredServiceIds?: number[] | null;
  /**
   * Whether the service check applies. False when the target column belongs to the same
   * staff member — a pure time shift cannot introduce a service they cannot perform.
   */
  checkServices: boolean;
};

/**
 * Runs every guardrail against a candidate drop.
 *
 * The service check comes first because it disqualifies the entire column regardless of
 * where in the day the card is held, which lets the UI grey out that column as a whole.
 */
export function validateMove(check: MoveCheck): MoveVerdict {
  if (check.checkServices) {
    const missing = missingServices(check.requiredServiceIds, check.offeredServiceIds);
    if (missing === null) return { ok: false, reason: "services_unknown" };
    if (missing.length > 0) return { ok: false, reason: "services_unsupported" };
  }

  if (!coversRange(check.intervals, check.startMin, check.durationMin)) {
    return { ok: false, reason: "outside_working_hours" };
  }

  if (findOverlap(check.occupied, check.startMin, check.durationMin, check.movingId)) {
    return { ok: false, reason: "slot_occupied" };
  }

  return OK;
}
